/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { parseISO, addDays, addWeeks, addMonths, addYears } from "date-fns";

export const formatMembershipDuration = (duration, durationType, _t) => {
  if (!duration || !durationType) return "";
  const unitKey = duration > 1 ? `${durationType}s` : durationType;

  return _t("%duration% %duration_type%", { duration, duration_type: _t(unitKey) });
};

// Cadence suffix for a price ("month", "2 weeks") — omits the leading "1".
export const formatRecurringPeriod = (duration, durationType, _t) => {
  if (!duration || !durationType) return "";
  if (duration === 1) return _t(durationType);

  return _t("%duration% %duration_type%", { duration, duration_type: _t(`${durationType}s`) });
};

const PERIOD_ADDERS = { day: addDays, week: addWeeks, month: addMonths, year: addYears };

// Next charge date = period start + one period; null if not computable.
export const getMembershipNextChargeDate = (periodStart, duration, durationType) => {
  const add = PERIOD_ADDERS[durationType];
  if (!periodStart || !duration || !add) return null;

  return add(parseISO(periodStart), duration);
};

// Shared recurring fields (price/currency/duration + next charge date) for a
// membership line, used by both mapMembershipRecurringInfo and
// getOptionalRecurringMembershipForItem.
const buildMembershipRecurringFields = (line, membership, invoice) => ({
  recurringPrice: line.recurring_price ?? membership.recurring_price,
  currency: line.currency ?? membership.currency ?? invoice.currency,
  duration: membership.duration,
  durationType: membership.duration_type,
  nextChargeDate: getMembershipNextChargeDate(line.period_start, membership.duration, membership.duration_type),
});

const mapMembershipRecurringInfo = (line, invoice) => {
  const membership = line.membership;
  const { nextChargeDate, ...recurringFields } = buildMembershipRecurringFields(line, membership, invoice);

  return {
    id: line.membership_id ?? membership.id,
    name: membership.name,
    ...recurringFields,
    nextDate: nextChargeDate,
  };
};

// Memberships that will recur: forced (is_recurring_only) always; optional only when
// the client opts in via auto-renew.
export const getRecurringMembershipsForDisplay = (invoice, optedIn = false) =>
  (invoice?.lines || [])
    .filter((line) => {
      const membership = line.membership;
      if (line.type !== "membership" || !membership?.is_recurring) return false;
      return membership.is_recurring_only || optedIn;
    })
    .map((line) => mapMembershipRecurringInfo(line, invoice));

// Opt-in recurring info for a summary item (is_recurring && !is_recurring_only), or null.
export const getOptionalRecurringMembershipForItem = (invoice, item) => {
  const line = (invoice?.lines || []).find(
    (l) =>
      (item.lineId != null && String(l.id) === String(item.lineId)) ||
      (l.type === "membership" && +l.membership_id === +item.id)
  );

  const membership = line?.membership;
  if (!membership || !membership.is_recurring || membership.is_recurring_only) return null;

  const { nextChargeDate, ...recurringFields } = buildMembershipRecurringFields(line, membership, invoice);

  return {
    ...recurringFields,
    endDate: nextChargeDate,
  };
};
